import { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { FiTrendingUp } from "react-icons/fi";

const views = [
  {
    key: "total",
    label: "Revenue",
    stroke: "#7a2238",
    gradientId: "revenueGradient",
  },
  {
    key: "posSales",
    label: "POS Sales",
    stroke: "#2563eb",
    gradientId: "posGradient",
  },
  {
    key: "onlineSales",
    label: "Online Sales",
    stroke: "#059669",
    gradientId: "onlineGradient",
  },
];

export default function SalesOverview({
  dailyTrend = [],
  onlineSalesVisible = true,
}) {
  const [activeView, setActiveView] = useState("total");

  const availableViews = onlineSalesVisible
    ? views
    : views.filter((view) => view.key !== "onlineSales");

  const currentView =
    availableViews.find((view) => view.key === activeView) ||
    availableViews[0];

  const totals = useMemo(
    () => ({
      revenue: dailyTrend.reduce(
        (total, day) => total + Number(day.total || 0),
        0,
      ),
      posSales: dailyTrend.reduce(
        (total, day) => total + Number(day.posSales || 0),
        0,
      ),
      onlineSales: dailyTrend.reduce(
        (total, day) => total + Number(day.onlineSales || 0),
        0,
      ),
    }),
    [dailyTrend],
  );

  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <p className="text-sm text-gray-500">Performance Overview</p>

          <h2 className="text-2xl font-bold mt-1">Last 7 Days Sales</h2>
        </div>

        <div className="flex gap-3 flex-wrap">
          {availableViews.map((view) => (
            <button
              key={view.key}
              onClick={() => setActiveView(view.key)}
              className={`px-4 h-10 rounded-xl font-medium transition ${
                currentView.key === view.key
                  ? "bg-primary-700 text-white"
                  : "border border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {view.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className={`grid gap-5 mb-8 ${
          onlineSalesVisible ? "grid-cols-3" : "grid-cols-2"
        }`}
      >
        <SummaryCard
          title="Revenue"
          value={formatMoney(totals.revenue)}
          subtitle="Invoices, POS and online orders"
        />

        <SummaryCard
          title="POS Sales"
          value={formatMoney(totals.posSales)}
          subtitle="Completed POS sales"
        />

        {onlineSalesVisible && (
          <SummaryCard
            title="Online Sales"
            value={formatMoney(totals.onlineSales)}
            subtitle="Confirmed online orders"
          />
        )}
      </div>

      <div className="h-[380px]">
        {dailyTrend.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyTrend}>
              <defs>
                <linearGradient
                  id={currentView.gradientId}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={currentView.stroke}
                    stopOpacity={0.35}
                  />

                  <stop
                    offset="95%"
                    stopColor={currentView.stroke}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f1f5f9"
              />

              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
              />

              <YAxis
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => formatCompactMoney(value)}
              />

              <Tooltip
                formatter={(value) => [
                  formatMoney(value),
                  currentView.label,
                ]}
                labelFormatter={(label) => `Date: ${label}`}
                contentStyle={{
                  borderRadius: 16,
                  border: "none",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                }}
              />

              <Area
                type="monotone"
                dataKey={currentView.key}
                stroke={currentView.stroke}
                strokeWidth={4}
                fill={`url(#${currentView.gradientId})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <FiTrendingUp size={34} className="text-gray-300 mb-3" />
            <p>No sales data is available for the last seven days.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ title, value, subtitle }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-5 min-w-0">
      <p className="text-sm text-gray-500">{title}</p>

      <h3 className="text-2xl lg:text-3xl font-bold mt-2 truncate">
        {value}
      </h3>

      <div className="flex items-center gap-2 mt-3 text-green-600 text-sm font-medium">
        <FiTrendingUp />
        <span className="truncate">{subtitle}</span>
      </div>
    </div>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatCompactMoney(value) {
  const amount = Number(value || 0);

  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`;
  }

  if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(0)}K`;
  }

  return `₹${amount}`;
}